import { Link, useNavigate } from 'react-router-dom'
import { useState, useEffect } from 'react'
import { getCardStats, getOrderStats } from '../services/api'
import { useAuthSession } from '../contexts/AuthSessionContext'
import Section from '../components/m1nt/Section'
import { M1ntButton } from '../components/m1nt/ui'
import HeroImmersive from '../components/m1nt/home/HeroImmersive'
import StatsStrip from '../components/m1nt/home/StatsStrip'
import TrustMarquee from '../components/m1nt/home/TrustMarquee'
import ClosingCta from '../components/m1nt/home/ClosingCta'
import CaseShowcase from '../components/m1nt/home/CaseShowcase'

const STEPS = [
  {
    number: '01',
    title: 'Submit to Lab',
    description: 'Upload photos, details, and shipping info',
  },
  {
    number: '02',
    title: 'M1NT credit',
    description: 'One credit per submission (covers your service)',
  },
  {
    number: '03',
    title: 'Ship & Grade',
    description: 'Follow Lab instructions—then professional grading',
  },
  { number: '04', title: 'Mint NFT', description: 'Receive your authenticated NFT' },
]

const TRUST_ITEMS = [
  'Blockchain Verified',
  'IPFS Storage',
  'NFC Authenticated',
  'OpenSea Compatible',
]

export default function Home() {
  const { isAuthenticated } = useAuthSession()
  const navigate = useNavigate()
  const [stats, setStats] = useState(null)

  useEffect(() => {
    loadStats()
  }, [])

  const loadStats = async () => {
    try {
      const [cardRes, orderRes] = await Promise.all([getCardStats(), getOrderStats()])
      setStats({
        cards: cardRes.data.data,
        orders: orderRes.data.data,
      })
    } catch (error) {
      console.error('Failed to load stats:', error)
    }
  }

  const openLoginModal = () => {
    window.dispatchEvent(new Event('openAuthModal'))
  }

  const headlineStats = [
    {
      value: Number(stats?.cards?.total) || 1250,
      suffix: stats?.cards?.total ? '' : '+',
      label: 'Cards Registered',
    },
    {
      value: Number(stats?.cards?.byStatus?.minted) || 850,
      suffix: stats?.cards?.byStatus?.minted ? '' : '+',
      label: 'NFTs Minted',
    },
    {
      value: Number(stats?.orders?.completed) || 500,
      suffix: stats?.orders?.completed ? '' : '+',
      label: 'Cards Graded',
    },
    {
      value: Number(stats?.orders?.averageGrade) || 9.2,
      decimals: 1,
      label: 'Average Grade',
    },
  ]

  return (
    <>
      <HeroImmersive
        cta={
          isAuthenticated ? (
            <M1ntButton solid onClick={() => navigate('/owner')}>
              My Cards
            </M1ntButton>
          ) : (
            <M1ntButton solid onClick={openLoginModal}>
              Login to Continue
            </M1ntButton>
          )
        }
      />

      <div className="mx-auto max-w-[1200px] px-[var(--gutter)] pb-[var(--section)] pt-6 sm:pt-10">
        <StatsStrip stats={headlineStats} />
      </div>

      <TrustMarquee items={TRUST_ITEMS} />

      <Section
        eyebrow="Features"
        title="Why M1NT?"
        subtitle="The future of collectible card authentication is here"
      >
        <CaseShowcase />
      </Section>

      <Section
        id="how-it-works"
        eyebrow="Process"
        title="How it works"
        subtitle="From physical card to authenticated NFT in four simple steps"
      >
        <ol className="grid gap-x-10 gap-y-10 sm:grid-cols-2 lg:grid-cols-4">
          {STEPS.map((step, index) => (
            <li key={step.number} className="relative pt-6">
              <span className="absolute inset-x-0 top-0 h-px bg-m1-line" aria-hidden="true" />
              <span
                className="m1nt-step-line absolute inset-x-0 top-0 h-px bg-white"
                style={{ '--m1nt-step-index': index }}
                aria-hidden="true"
              />
              <span className="font-m1-mono text-[12px] tracking-[0.18em] text-m1-ink-soft">
                {step.number}
              </span>
              <h3 className="mt-4 font-m1-display text-[26px] font-normal leading-tight tracking-[-0.01em] text-m1-ink">
                {step.title}
              </h3>
              <p className="mt-3 text-[16px] font-light leading-relaxed text-m1-ink-soft">
                {step.description}
              </p>
            </li>
          ))}
        </ol>
      </Section>

      <ClosingCta
        eyebrow="Get started"
        title="Ready to authenticate your collection?"
        subtitle="Join collectors worldwide who trust M1NT for professional grading and NFT authentication."
        actions={
          <M1ntButton solid as={Link} to="/owner" state={{ openSubmitModal: true }}>
            Start Now
            <i className="ri-arrow-right-line text-lg" aria-hidden="true" />
          </M1ntButton>
        }
      />
    </>
  )
}
